import * as React from 'react';
import { Alert, Button, Modal, Select } from '@ixicare/ui';
import { parseDeviceQr, TParsedDeviceCode } from '@ixicare/resources';
import { t } from '~i18n';
import { useCameraScanner, ScannerError } from './useCameraScanner';
import styles from './ScannerModal.module.css';

export type ScanResult = TParsedDeviceCode & { raw: string };

interface ScannerModalProps {
  onClose: () => void;
  /** Fired once with the parsed code. The modal closes itself right after. */
  onScan: (result: ScanResult) => void;
  /** Overrides the default "hold the QR or barcode in front of the camera" hint. */
  hint?: string;
  /**
   * Return a message to refuse a scan. The camera stays live and keeps reading,
   * so the next code can be presented without reopening the scanner.
   */
  validate?: (result: ScanResult) => string | undefined;
}

const errorMessages: Record<ScannerError, () => string> = {
  insecure: () => t('deviceScan.error.insecure'),
  unsupported: () => t('deviceScan.error.unsupported'),
  permission: () => t('deviceScan.error.permission'),
  noCamera: () => t('deviceScan.error.noCamera'),
  unknown: () => t('deviceScan.error.unknown'),
};

export const ScannerModal: React.FC<ScannerModalProps> = ({ onClose, onScan, hint, validate }) => {
  const [deviceId, setDeviceId] = React.useState<string>();
  const [rejection, setRejection] = React.useState<string>();
  // A continuous decode fires repeatedly on the same code; latch so the caller
  // only ever sees the first hit.
  const handled = React.useRef(false);

  const handleDecoded = React.useCallback(
    (value: string) => {
      if (handled.current) return;
      const result: ScanResult = { ...parseDeviceQr(value), raw: value };

      const refusal = validate?.(result);
      if (refusal) {
        // Deliberately not latched: the decode loop keeps running so the user
        // can hold up an acceptable code without reopening the scanner. The
        // same rejected code re-decodes to the same string, so this is a no-op
        // until something different comes into view.
        setRejection(refusal);
        return;
      }

      handled.current = true;
      onScan(result);
      onClose();
    },
    [onScan, onClose, validate]
  );

  const { videoRef, error, isReady, cameras, activeDeviceId } = useCameraScanner({
    onDecoded: handleDecoded,
    deviceId,
  });

  const cameraOptions = React.useMemo(
    () =>
      cameras.map((camera, index) => ({
        value: camera.deviceId,
        label: camera.label || t('deviceScan.cameraFallback', { index: index + 1 }),
      })),
    [cameras]
  );

  return (
    <Modal onClose={onClose} className={styles.modal}>
      <h3 className={styles.title}>{t('deviceScan.title')}</h3>
      <p className={styles.hint}>{hint ?? t('deviceScan.hint')}</p>

      {error ? (
        <Alert type="error">{errorMessages[error]()}</Alert>
      ) : (
        <>
          <div className={styles.viewport}>
            {/* muted + playsInline are what let iOS Safari autoplay the preview. */}
            <video ref={videoRef} className={styles.video} muted playsInline />
            {isReady ? (
              <div className={styles.reticle}>
                <div className={styles.window}>
                  <span className={styles.corners} />
                </div>
              </div>
            ) : (
              <div className={styles.status}>{t('deviceScan.starting')}</div>
            )}
          </div>
          {rejection && (
            <div className={styles.rejection}>
              <Alert type="error">{rejection}</Alert>
            </div>
          )}
          {cameraOptions.length > 1 && (
            <div className={styles.cameraSelect}>
              <Select
                label={t('deviceScan.camera')}
                options={cameraOptions}
                value={deviceId ?? activeDeviceId ?? cameraOptions[0].value}
                onChange={setDeviceId}
              />
            </div>
          )}
        </>
      )}

      <div className={styles.footer}>
        <Button onClick={onClose} isBlock buttonType="link">
          {t('global.cancel')}
        </Button>
      </div>
    </Modal>
  );
};
